import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import { useRolodex } from "../hooks/useRolodex";
import { useDiscoveryGraph } from "../hooks/useDiscoveryGraph";
import { useSearch } from "../hooks/useSearch";
import SearchBar from "../components/SearchBar";
import PathResultCard from "../components/PathResultCard";
import RolodexResultCard from "../components/RolodexResultCard";
import ContextSwitcher from "../components/ContextSwitcher";
import NetworkGraph from "../components/NetworkGraph";
import { buildContextGraph, rolodexPersonId } from "../lib/contextGraph";
import { graphView } from "../lib/graphView";
import { filterEntriesByContext, searchRolodex } from "../lib/rolodexContext";
import Nav from "../components/Nav";


export default function Search() {
  const { user } = useAuth();
  const { profile } = useProfile(user?.uid);
  const { entries, loading: rolodexLoading } = useRolodex(user?.uid);
  const discovery = useDiscoveryGraph(user?.uid, entries);
  const navigate = useNavigate();
  const [scope, setScope] = useState("extended");
  const [context, setContext] = useState("all");
  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlightedPath, setHighlightedPath] = useState(null);
  const [mobileTab, setMobileTab] = useState("list");
  const graphRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(rawQuery), 300);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  useEffect(() => {
    setHighlightedPath(null);
    graphRef.current?.resetHighlight();
  }, [debouncedQuery, scope, context]);

  const nodeMap = useMemo(
    () => new Map(discovery.nodes.map((node) => [node.id, node])),
    [discovery.nodes]
  );
  const extendedResults = useSearch(discovery.nodes, debouncedQuery);
  const rolodexResults = useMemo(
    () => debouncedQuery ? searchRolodex(entries, debouncedQuery, context) : [],
    [entries, debouncedQuery, context]
  );

  const extendedTargetIds = useMemo(
    () => new Set(extendedResults.map((result) => result.targetNode.id)),
    [extendedResults]
  );
  const extendedMatchedIds = useMemo(
    () => new Set(extendedResults.flatMap((result) => result.path)),
    [extendedResults]
  );
  const privateGraph = useMemo(
    () => buildContextGraph(entries, user?.uid, context, profile),
    [entries, user?.uid, context, profile]
  );
  const privateMatchedIds = useMemo(
    () => new Set(rolodexResults.map(rolodexPersonId)),
    [rolodexResults]
  );
  const extendedGraph = useMemo(
    () => graphView(
      discovery.nodes,
      discovery.edges,
      debouncedQuery
        ? { context: "search", matchedIds: extendedTargetIds }
        : { context: "near" }
    ),
    [discovery.nodes, discovery.edges, debouncedQuery, extendedTargetIds]
  );
  const graph = scope === "rolodex" ? privateGraph : extendedGraph;
  const matchedNodeIds = scope === "rolodex" ? privateMatchedIds : extendedMatchedIds;
  const loading = scope === "rolodex" ? rolodexLoading : discovery.loading;
  const results = scope === "rolodex" ? rolodexResults : extendedResults;
  const counts = useMemo(() => ({
    professional: filterEntriesByContext(entries, "professional").length,
    personal: filterEntriesByContext(entries, "personal").length,
    all: filterEntriesByContext(entries, "all").length,
  }), [entries]);

  const handleExtendedResultClick = (result) => {
    setHighlightedPath(result.path);
    setMobileTab("graph");
    graphRef.current?.highlightPath(result.path);
  };

  const handleRolodexResultClick = (entry) => {
    const targetContext = context === "all"
      ? entry.facets?.professional ? "professional" : "personal"
      : context;
    window.localStorage.setItem("aline:rolodex-context", targetContext);
    navigate(`/connections/${encodeURIComponent(entry.id)}`);
  };

  const clearHighlightedPath = () => {
    setHighlightedPath(null);
    graphRef.current?.resetHighlight();
  };

  return (
    <div className="search-page">
      <Nav profile={profile} active="search" inline />

      <div className="search-mobile-tabs">
        <button className={`search-tab-btn${mobileTab === "list" ? " active" : ""}`} onClick={() => setMobileTab("list")}>Results</button>
        <button className={`search-tab-btn${mobileTab === "graph" ? " active" : ""}`} onClick={() => setMobileTab("graph")}>Network</button>
      </div>

      <div className="search-layout">
        <div className={`search-left-panel${mobileTab === "graph" ? " mobile-hidden" : ""}`}>
          <div className="search-left-header context-search-header">
            <div className="search-scope-switch" role="radiogroup" aria-label="Search scope">
              <button
                role="radio"
                aria-checked={scope === "rolodex"}
                className={scope === "rolodex" ? "is-active" : ""}
                onClick={() => setScope("rolodex")}
              >
                My rolodex
              </button>
              <button
                role="radio"
                aria-checked={scope === "extended"}
                className={scope === "extended" ? "is-active" : ""}
                onClick={() => setScope("extended")}
              >
                Extended network
              </button>
            </div>
            {scope === "rolodex" && (
              <ContextSwitcher value={context} onChange={setContext} counts={counts} hideLabel className="search-context-switcher" />
            )}
            <SearchBar
              value={rawQuery}
              onChange={setRawQuery}
              loading={loading && !graph.nodes.length}
              placeholder={scope === "rolodex" ? "Search names, details, notes, or organizations…" : "Search role, industry, company, school, or organization…"}
            />
          </div>

          <div className="search-results-area">
            {loading && !graph.nodes.length ? (
              <div className="search-loading-state"><div className="spinner" /><p className="search-loading-text">Loading…</p></div>
            ) : !debouncedQuery ? (
              <div className="search-prompt-card">
                <div className={`search-prompt-icon scope-${scope}`} aria-hidden="true">{scope === "rolodex" ? "R" : "↗"}</div>
                <p className="search-prompt-title">
                  {scope === "rolodex" ? "Find someone you already know" : "Find a warm path outward"}
                </p>
                <p className="search-prompt-body">
                  {scope === "rolodex"
                    ? "Search your private names, notes, relationship details, and public profile information."
                    : "Search public profiles reached through professional and explicitly opted-in personal connections."}
                </p>
                {scope === "extended" && discovery.truncated && (
                  <p className="search-truncation-note">Network preview limited to 500 people for performance.</p>
                )}
              </div>
            ) : results.length === 0 ? (
              <div className="search-empty-state">
                <p className="search-empty-title">No results for “{debouncedQuery}”</p>
                <p className="search-empty-body">
                  {scope === "rolodex" ? "Try another context or a detail from their private contact card." : "Try a different role, industry, company, school, or organization."}
                </p>
              </div>
            ) : (
              <>
                <div className="search-results-header">
                  <span className="search-results-count">{results.length} result{results.length !== 1 ? "s" : ""}</span>
                  {highlightedPath && (
                    <button className="search-clear-highlight-btn" onClick={clearHighlightedPath}>Clear highlight</button>
                  )}
                </div>
                <div className="search-results-list">
                  {scope === "rolodex"
                    ? rolodexResults.map((entry) => (
                        <RolodexResultCard key={entry.id} entry={entry} context={context} onClick={handleRolodexResultClick} />
                      ))
                    : extendedResults.map((result) => (
                        <PathResultCard
                          key={result.targetNode.id}
                          result={result}
                          nodeMap={nodeMap}
                          isHighlighted={highlightedPath?.at(-1) === result.targetNode.id}
                          onClick={() => handleExtendedResultClick(result)}
                        />
                      ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={`search-right-panel${mobileTab === "list" ? " mobile-hidden" : ""}`}>
          <div className="graph-panel">
            <div className="graph-panel-header">
              <span className="eyebrow">{scope === "rolodex" ? `${context} map` : "Introduction network"}</span>
              <div className="graph-controls">
                <button className="graph-ctrl-btn" onClick={() => graphRef.current?.zoomIn()} aria-label="Zoom in">+</button>
                <button className="graph-ctrl-btn" onClick={() => graphRef.current?.zoomOut()} aria-label="Zoom out">−</button>
                <button className="graph-ctrl-btn" onClick={() => graphRef.current?.resetView()} aria-label="Reset view">⊙</button>
              </div>
            </div>
            {loading && !graph.nodes.length ? (
              <div className="graph-loading-state"><div className="spinner" /></div>
            ) : (
              <NetworkGraph
                ref={graphRef}
                nodes={graph.nodes}
                edges={graph.edges}
                matchedNodeIds={matchedNodeIds.size ? matchedNodeIds : null}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
